import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { Button, TextField } from "@mui/material";

import { ColumnDef } from "@tanstack/react-table";

import {
  CheckedItems,
  OutputFormat,
  SetCheckedItems,
  setOutputFormat,
} from "pages/geoscape-batch/models";
import { InputBatchConfigFormat } from "pages/geoscape-batch/types";
import { Dispatch, SetStateAction } from "react";
import { MANDATORY_ATTRIBUTES, addIndex, transformString } from "./utils";

type IndexedInput = InputBatchConfigFormat & { index: number };

interface BuildInputColumnsParams {
  setInPutParameters: Dispatch<SetStateAction<InputBatchConfigFormat[]>>;
}

export const buildInputColumns = ({
  setInPutParameters,
}: BuildInputColumnsParams): ColumnDef<IndexedInput>[] => [
  {
    accessorKey: "index",
    header: "#",
    size: 50,
  },
  {
    accessorKey: "name",
    header: "Name",
    size: 250,
    cell: ({ row }) => {
      return (
        <TextField
          id={`input-name-${row.original.index}`}
          label="Column Name"
          size="small"
          value={row.getValue("name") ?? ""}
          variant="outlined"
          onChange={(e) => {
            const newName = e.target.value;
            setInPutParameters((prev) =>
              prev.map((item, i) =>
                i === row.original.index - 1 ? { ...item, name: newName } : item,
              ),
            );
          }}
        />
      );
    },
  },
  {
    accessorKey: "apiParameter",
    header: "Api Paramer",
    size: 250,
    cell: ({ row }) => (
      <TextField
        id={`input-api-${row.original.index}`}
        label="Attribute"
        size="small"
        value={row.getValue("apiParameter")}
        variant="outlined"
        disabled={row.getValue("apiParameter") === "Ignore"}
      />
    ),
  },
];

interface BuildColumnsParams {
  enrichmentRows: OutputFormat[];
  setEnrichmentRows: setOutputFormat;

  setCheckedItems: SetCheckedItems;
}

export const buildColumns = ({
  enrichmentRows,
  setEnrichmentRows,
  setCheckedItems,
}: BuildColumnsParams): ColumnDef<OutputFormat, unknown>[] => {
  const handleDeleteColumn = (index: number) => {
    const updateEnrichmentRows = [...enrichmentRows];

    const deletedEnrichmentRow = updateEnrichmentRows.splice(index - 1, 1)[0];

    setCheckedItems((prev: CheckedItems) => ({
      ...prev,
      [deletedEnrichmentRow.property]: {
        ...prev[deletedEnrichmentRow.property],
        [`${deletedEnrichmentRow.attributeName}`]: false,
      },
    }));

    setEnrichmentRows(addIndex(updateEnrichmentRows));
  };

  return [
    {
      enableSorting: true,
      accessorKey: "index",
      header: "#",
      size: 50,
    },
    {
      accessorKey: "name",
      header: "Name",
      size: 220,
      cell: ({ row }) => {
        return (
          <TextField
            id={`enrichment-name-${row.original.index}`}
            label="Column Name"
            size="small"
            value={row.getValue("name") ?? ""}
            variant="outlined"
            onChange={(e) => {
              const newName = e.target.value;
              setEnrichmentRows((prev) =>
                prev.map((item, i) =>
                  i === (row.original.index as number) - 1
                    ? { ...item, name: newName }
                    : item,
                ),
              );
            }}
          />
        );
      },
    },

    {
      accessorKey: "attributeName",
      header: "Attribute Name",
      size: 220,
      cell: ({ row }) => (
        <TextField
          id={`enrichment-attr-${row.original.index}`}
          label={
            row.original?.property
              ? transformString(row.original?.property)
              : ""
          }
          size="small"
          value={transformString(row.getValue("attributeName"))}
          variant="outlined"
        />
      ),
    },
    {
      accessorKey: "actions",
      header: "Actions",
      size: 60,
      cell: ({ row }) => {
        return (
          <Button
            variant="text"
            color="error"
            onClick={() => {
              handleDeleteColumn(row.original.index as number);
            }}
            disabled={MANDATORY_ATTRIBUTES[row.original.property]?.includes(
              row.original.attributeName
            )}
          >
            <DeleteIcon />
          </Button>
        );
      },
    },
  ];
};
